import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { getDataStore } from "@/data/dataStore.js";
import { getBackendToken } from "@/api/karateBackendApi.js";
import {
  apiFetchDojos,
  apiFetchInstructeurs,
  apiFetchStudents,
  apiFetchStats,
  apiFetchParents,
} from "@/api/karateBackendApi.js";
import { getDataMode, isBackendConfigured, isHybridDataMode } from "@/config/backendStaging049.js";
import { KARATE_AVATAR_EVENT } from "@/services/karateAvatarStorage.js";

function subscribe(cb) {
  window.addEventListener("karate-data-updated", cb);
  window.addEventListener("karate-backend-auth-updated", cb);
  window.addEventListener("karate-avatar-updated", cb);
  return () => {
    window.removeEventListener("karate-data-updated", cb);
    window.removeEventListener("karate-backend-auth-updated", cb);
    window.removeEventListener("karate-avatar-updated", cb);
  };
}

export function useHybridDataStore() {
  const localStore = useSyncExternalStore(subscribe, () => getDataStore(), () => getDataStore());
  const [backendSlice, setBackendSlice] = useState(null);
  const [backendStats, setBackendStats] = useState(null);
  const [backendStatus, setBackendStatus] = useState("idle");
  const hybrid = isHybridDataMode();
  const token = getBackendToken();

  const refreshBackend = useCallback(async () => {
    if (!hybrid || !token) {
      setBackendSlice(null);
      setBackendStats(null);
      setBackendStatus(token ? "idle" : "local");
      return;
    }
    setBackendStatus("loading");
    try {
      const [dojosRes, instRes, stuRes, statsRes, parentsRes] = await Promise.all([
        apiFetchDojos().catch(() => null),
        apiFetchInstructeurs().catch(() => null),
        apiFetchStudents().catch(() => null),
        apiFetchStats().catch(() => null),
        apiFetchParents().catch(() => null),
      ]);
      if (dojosRes || instRes || stuRes) {
        setBackendSlice({
          dojos: dojosRes?.dojos,
          instructeurs: instRes?.instructeurs,
          etudiants: stuRes?.students,
          parents: parentsRes?.parents,
        });
        setBackendStats(statsRes?.stats || null);
        setBackendStatus("backend");
      } else {
        setBackendSlice(null);
        setBackendStatus("fallback");
      }
    } catch {
      setBackendSlice(null);
      setBackendStatus("fallback");
    }
  }, [hybrid, token]);

  useEffect(() => {
    refreshBackend();
  }, [refreshBackend]);

  useEffect(() => {
    const onAvatarUpdated = () => refreshBackend();
    window.addEventListener(KARATE_AVATAR_EVENT, onAvatarUpdated);
    return () => window.removeEventListener(KARATE_AVATAR_EVENT, onAvatarUpdated);
  }, [refreshBackend]);

  const store = useMemo(() => {
    if (!backendSlice || backendStatus !== "backend") {
      return { ...localStore, _dataSource: "local" };
    }
    return {
      ...localStore,
      dojos: backendSlice.dojos ?? localStore.dojos,
      instructeurs: (backendSlice.instructeurs ?? localStore.instructeurs)?.map((row) => ({
        ...row,
        _avatarFromBackend: true,
      })) ?? localStore.instructeurs,
      etudiants: backendSlice.etudiants ?? localStore.etudiants,
      parents: backendSlice.parents ?? localStore.parents ?? [],
      _dataSource: "backend",
      _backendStats: backendStats,
    };
  }, [localStore, backendSlice, backendStats, backendStatus]);

  return {
    store,
    dataMode: getDataMode(),
    backendConfigured: isBackendConfigured(),
    backendStatus,
    backendStats,
    refreshBackend,
    isHybrid: hybrid,
  };
}
